// @ts-strict-ignore
import memoizeLast from 'just-memoize-last';
import isEqual from 'lodash/isEqual';

import {LocalizationStore} from 'common/i18n/store';
import {
  BehaviorShowClause,
  InsightsQueryResults,
  InsightsSeriesTree,
  ShowClause,
} from 'common/report/insights/util/types';
import {
  ChartType,
  InsightsBookmarkParams,
  MetricType,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  RetentionType,
  RetentionUnboundedModeType,
  SortBy,
  SortConfig,
  SortOrder,
} from 'common/types/reports/bookmark';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {Report} from 'common/report/insights/models/report';
import {isEmptyRetentionResult} from 'common/report/insights/util';
import {
  BirthIntervalUnit,
  FormattedRow,
  LiftRow,
  MetricKeyInfo,
  RawCell,
  RetentionDataTableRow,
} from 'common/report/retention/util/types';
import {getUiRowsDataFromParsed} from 'common/report/retention/util/parsing';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {BookmarkDateRange, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getEventDisplayName, getRetentionBucketName} from 'common/report/retention/util';
import {getDefaultSortConfig} from 'common/report/util/sorting/table-sorting';
import {flattenMultipleRetentionMetrics, flattenSegments} from 'common/report/retention/util/request';
import {KeyedByDate, Retention, RetentionData, SegmentKeyToPath} from 'common/report/retention/models/types';
import {getDataGroupName} from 'common/report/retention/util/format';
import {RetentionPopover} from 'common/report/retention/visualization/popover';
import {formatMetricNumber, formatMetricNumberWithUnits, formatPercent} from 'common/util';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {isRetentionShowClause} from 'common/report/insights/models/utils';
import {
  getDefaultPrimaryAndSecondaryAxisOptions,
  getXAxisDefaultOptions,
  getYAxisDefaultOptions,
} from 'common/query-builder/chart-customization/utils';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {Translatable} from 'common/i18n';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {CommonDataTableFields} from 'common/components/data-table/types';
import {PropertyDisplayOptions} from 'apps/lexicon/src/models/property-definition';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {
  getDisplayOptionsFromLineChartYAxisOptions,
  getFinalDisplayOptions,
  getMetricFormattingDefaultsFromChartParams,
  mergeDisplayOptions,
  metricFormatterFunctionForAxis,
} from 'common/report/insights/charts/util';
import {LineChartYAxisOptions} from 'common/charts/visualization/line-chart/types';

type GetCurveChartDataArgs = {
  timezone: string;
  queryResults: InsightsQueryResults<Retention>;
  segments: Segment[];
  metricKeyLookup: Record<string, MetricKeyInfo> | null;
  bookmark: InsightsBookmarkParams;
  chartType: ChartType;
  i18n: LocalizationStore;
};

export class RetentionBookmarkController {
  declare bookmark: InsightsBookmarkParams;
  declare _queryResults: InsightsQueryResults<Retention>;
  _metricKeyLookup: Record<string, MetricKeyInfo> | null = null;

  private declare memoizedGetCurveChartData: (args: GetCurveChartDataArgs) => {
    chartRows: Array<FormattedRow<RawCell>>;
    formattedLiftRowsData: Map<string, LiftRow>;
    segmentKeyToPath: SegmentKeyToPath;
  };

  constructor(bookmark: InsightsBookmarkParams, results: InsightsQueryResults<Retention>) {
    this.bookmark = bookmark;
    this._queryResults = results;
    this.memoizedGetCurveChartData = memoizeLast(this._getCurveChartData.bind(this), isEqual);
  }

  set queryResults(results: InsightsQueryResults<Retention>) {
    this._queryResults = results;
    this._metricKeyLookup = null;
  }

  get queryResults(): InsightsQueryResults<Retention> {
    return this._queryResults;
  }

  /**
   * Returns a map that whose key is a metric key and value is an object of information
   * for metric key - isHidden and showClauseIndex, which indicates whether the given metric
   * is hidden in the report and its index in show section, respectively.
   * metricKeyLookup is defined if there are multiple retention show clauses
   * i.e. {
   *    [metric_key]: {
   *        isHidden: boolean;
   *        showClauseIndex: number;
   *    }
   * }
   * returns a map of metric key and information
   * It is null if there are only one retention show clause
   * It is defined if there are multiple retention show clauses
   */
  get metricKeyLookup(): Record<string, MetricKeyInfo> | null {
    if (this._metricKeyLookup) return this._metricKeyLookup;

    const retentionMetrics = this.getRetentionShowClauses();
    if (retentionMetrics.length > 1) {
      this._metricKeyLookup = {};
      const showSection = this.bookmark.sections.show;
      this.queryResults.meta.report_sections.show.forEach((show, index) => {
        const isHidden = showSection[index].isHidden;
        this._metricKeyLookup[show.metric_key] = {
          isHidden: Boolean(isHidden),
          showClauseIndex: index,
        };
      });
    }

    return this._metricKeyLookup;
  }

  public hasResults(): boolean {
    return !isEmptyRetentionResult(this.queryResults);
  }

  public getTimeSeries(path: string[]): InsightsSeriesTree<Retention> {
    let timeSeries: InsightsSeriesTree<Retention> = this.queryResults.series;
    path.forEach((seg) => {
      timeSeries = timeSeries[seg] as InsightsSeriesTree<Retention>;
    });
    return timeSeries;
  }

  public getChartType(): ChartType {
    return this.bookmark.displayOptions.chartType;
  }

  public getAxisAssignment(showIndex: number): BookmarkTypes.AxisAssignment {
    const showClause = this.bookmark.sections.show[showIndex];
    return showClause.display?.axis || BookmarkTypes.AxisAssignment.Primary;
  }

  public getLegendSelectedSegments(): null | string[] {
    const individualSelectedSegments = this.bookmark.legend?.individualSelectedSegments;
    if (!individualSelectedSegments) return null;

    return (
      individualSelectedSegments &&
      individualSelectedSegments.selected &&
      individualSelectedSegments.selected.map((item) => item.key)
    );
  }

  public getLegendExpandedSegments(): string[] {
    const individualSelectedSegments = this.bookmark.legend?.individualSelectedSegments;
    if (!individualSelectedSegments) return [];

    return (
      (individualSelectedSegments &&
        individualSelectedSegments.expanded &&
        individualSelectedSegments.expanded.map((item) => item.key)) ||
      []
    );
  }

  public getMetricKeyToShowClauseMap(): Record<string, ShowClause> {
    if (!this.metricKeyLookup) return null;
    return Object.entries(this.metricKeyLookup).reduce(
      (acc, [metricKey, {showClauseIndex}]) => {
        acc[metricKey] = this.bookmark.sections.show[showClauseIndex];
        return acc;
      },
      {} as Record<string, ShowClause>,
    );
  }

  /**
   * Given a retention table row returns the matching show clause for the retention metric.
   * @param row
   * @param metricKeyToShowClauseMap We parameterize on this so that it can be
   * computed once earlier then passed in to avoid recomputing it every time a
   * cell is rendered.
   * @returns
   */
  public getShowClauseFromRetentionTableRow(
    row: RetentionDataTableRow | FormattedRow<RawCell>,
    metricKeyToShowClauseMap,
  ): ShowClause {
    const metricKey = row[CommonDataTableFields.SegmentPrefix + `0`];
    // TWO CASES
    // 1) There's only one retention metric there won't be a metricKeyToShowClauseMap
    // in which case we just return the first show clause.
    if (!metricKeyToShowClauseMap) {
      return this.bookmark.sections.show[0];
    }
    // 2) There are multiple retention metrics and we need to look up the show clause in the map
    return metricKeyToShowClauseMap[metricKey];
  }

  public isPercentageView(showIndex: number): boolean {
    const measurement = (this.bookmark.sections.show[showIndex] as BehaviorShowClause).measurement;
    return measurement.math === RETENTION_MATH_TYPES.RETENTION_RATE;
  }

  public isCumulative(showIndex: number): boolean {
    const showClause = this.bookmark.sections.show[showIndex] as BehaviorShowClause;
    return showClause.measurement.retentionCumulative;
  }

  getUnboundedMode(showIndex: number): RetentionUnboundedModeType {
    const showClause = this.bookmark.sections.show[showIndex] as BehaviorShowClause;
    return showClause.behavior.retentionUnboundedMode;
  }

  public getLiftComparison(): LiftComparison {
    return this.bookmark.liftComparison;
  }

  public getSorting(): SortConfig {
    const segmentationLevel = this.getBreakdownCount();
    const sortConfig = this.bookmark.sorting[ChartType.RetentionCurve];
    return (
      sortConfig ||
      getDefaultSortConfig({
        numSegmentColumns: segmentationLevel || 1,
        valueSortField: `size`,
        flatSortConfig: {
          sortBy: SortBy.Value,
          sortOrder: SortOrder.Descending,
        },
      })
    );
  }

  public popoverFormatter(metricsResource: MetricsResource, matchingShowClauseIndex: number) {
    const showClause = this.bookmark.sections.show[matchingShowClauseIndex];
    const isPercentShowClause = this.isPercentageView(matchingShowClauseIndex);
    return (value: number, isPercentValue: boolean) => {
      let options: Partial<PropertyDisplayOptions> = {
        // Never abbreviate values in popovers.
        abbrev: false,
        suffix: isPercentValue ? `%` : ``,
      };
      if (isPercentValue) {
        value *= 100;
      }
      // We only want to format the value if it matches the math type of the show
      // clause. For example if the math type is "Retention Rate" we only want to
      // format the percent value (the actual retention rate) and not the absolute
      // value and vice versa if our math type is Unique Users or something else.
      if (isPercentShowClause === isPercentValue) {
        // Never abbreviate values in popovers.
        // This time we define it as an override so that the user's preference
        // can't affect it.
        options = getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metricsResource, options, {
          abbrev: false,
        });
      }
      return formatMetricNumberWithUnits(value, options);
    };
  }

  public getBornEvent(showIndex: number): string {
    const behaviors = (this.bookmark.sections.show[showIndex] as BehaviorShowClause).behavior.behaviors || [];
    return behaviors[0]?.name || ``;
  }
  public getEvent(showIndex: number): string {
    const behaviors = (this.bookmark.sections.show[showIndex] as BehaviorShowClause).behavior.behaviors || [];
    return behaviors[1]?.name || ``;
  }
  public getRetentionBucketIndex(showIndex: number): number {
    return (this.bookmark.sections.show[showIndex] as BehaviorShowClause).measurement.retentionBucketIndex;
  }
  public getPropertyAggregation(showIndex: number): string {
    const math = (this.bookmark.sections.show[showIndex] as BehaviorShowClause).measurement.math;
    const property = (this.bookmark.sections.show[showIndex] as BehaviorShowClause).measurement.property;

    if (math === RETENTION_MATH_TYPES.PROPERTY_SUM && property) {
      return `property sum`;
    } else if (math === RETENTION_MATH_TYPES.PROPERTY_AVERAGE) {
      return `property average`;
    }

    return undefined;
  }

  getRetentionAlignmentType(showIndex: number): RetentionAlignmentType {
    const showClause = this.bookmark.sections.show[showIndex] as BehaviorShowClause;
    return showClause.behavior.retentionAlignmentType;
  }

  public getSegments(): Segment[] {
    return getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses);
  }

  getRetentionShowClauses(): BehaviorShowClause[] {
    return this.bookmark.sections.show.filter(isRetentionShowClause);
  }

  public getVisibleRetentionMetrics(): BehaviorShowClause[] {
    const retentionMetrics = this.getRetentionShowClauses();
    return retentionMetrics.filter((clause) => !clause.isHidden);
  }

  public getRetentionType(showIndex?: number): RetentionType {
    if (showIndex >= 0) {
      return (this.bookmark.sections.show[showIndex] as BehaviorShowClause).behavior.retentionType;
    }
    const retentionShowClauses = this.getRetentionShowClauses();
    const isAddiction =
      retentionShowClauses.length === 1 && retentionShowClauses[0].behavior.type === MetricType.RetentionFrequency;
    return isAddiction ? `addiction` : `birth`;
  }
  public getRetentionUnit(showIndex?: number): TimeUnit {
    if (showIndex >= 0) {
      return (this.bookmark.sections.show[showIndex] as BehaviorShowClause).behavior.retentionUnit;
    }
    const retentionShowClauses = this.getRetentionShowClauses();
    return retentionShowClauses[0].behavior.retentionUnit;
  }

  public getBucketSizes(showIndex?: number): number[] {
    if (showIndex >= 0) {
      return (this.bookmark.sections.show[showIndex] as BehaviorShowClause).behavior.retentionCustomBucketSizes || [];
    }
    const retentionShowClauses = this.getRetentionShowClauses();
    return retentionShowClauses[0].behavior.retentionCustomBucketSizes || [];
  }

  public getDateUnit(): BirthIntervalUnit {
    return this.bookmark.sections.time[0].unit;
  }
  public getDateRange(): BookmarkDateRange {
    const time = this.queryResults.date_range;
    const dateRange = new DateRange({
      type: DateRangeType.Between,
      from: time.from_date,
      to: time.to_date,
    });
    return dateRange.getBookmarkFormat();
  }

  getDataGroupId() {
    return this.bookmark.sections.globalDataGroupId;
  }

  public getDataGroupName(plural = false): string {
    return getDataGroupName(this.getDataGroupId(), plural);
  }

  public getIncompletBuckets() {
    const firstRetentionMetricIndex = this.bookmark.sections.show.findIndex(isRetentionShowClause);
    return this.queryResults.meta.report_sections.show[firstRetentionMetricIndex].incomplete_buckets;
  }

  public getPrimaryYAxisOptions() {
    return this.bookmark.displayOptions.primaryYAxisOptions;
  }

  public getSecondaryYAxisOptions() {
    return this.bookmark.displayOptions.secondaryYAxisOptions;
  }

  public getYAxisDefaultOptions(metricsResource: MetricsResource, axis: BookmarkTypes.AxisAssignment) {
    return getYAxisDefaultOptions(this.bookmark, axis, metricsResource);
  }

  public getXAxisOptions() {
    return this.bookmark.displayOptions.xAxisOptions;
  }

  public getXAxisDefaultOptions() {
    return getXAxisDefaultOptions(this.bookmark, dataGroupStore);
  }

  public createPopover({
    matchingShowClauseIndex,
    projectTimezone,
    colIdx,
    rowIdx,
    groupedRowIdx = null,
    absoluteValue,
    absoluteDropOffValue,
    cohortSize,
    completeDate,
    enteredValue,
    enteredDate,
    incomplete,
    isAggregated,
    i18n,
  }: {
    matchingShowClauseIndex: number;
    projectTimezone: string;
    colIdx: number; // matching trend index
    rowIdx: number; // matching bucket index
    groupedRowIdx?: number;
    absoluteValue: string;
    absoluteDropOffValue: string;
    cohortSize: number;
    completeDate: string | false;
    enteredValue: string;
    enteredDate?: Date;
    incomplete: boolean;
    isAggregated: boolean;
    i18n: LocalizationStore;
  }): RetentionPopover {
    const bornEvent = this.getBornEvent(matchingShowClauseIndex);
    const event = this.getEvent(matchingShowClauseIndex);

    return new RetentionPopover(
      {
        matchingShowClauseIndex,
        bornEventName: bornEvent && getEventDisplayName(bornEvent),
        bucketSizes: this.getBucketSizes(matchingShowClauseIndex),
        dataGroupName: this.getDataGroupName(false),
        dataGroupNamePlural: this.getDataGroupName(true),
        eventName: getEventDisplayName(event),
        groupBy: this.getSegments(),
        retentionUnit: this.getRetentionUnit(matchingShowClauseIndex),
        dateUnit: this.getDateUnit(),
        dateRange: this.getDateRange(),
        retentionType: this.getRetentionType(matchingShowClauseIndex),
        retentionAlignmentType: this.getRetentionAlignmentType(matchingShowClauseIndex),
        unboundedMode: this.getUnboundedMode(matchingShowClauseIndex),
        isCumulative: this.isCumulative(matchingShowClauseIndex),
        chartType: this.getChartType(),
        projectTimezone,
        metricLabel: null,
        bookmark: this.bookmark,
        queryResults: this._queryResults,
      },
      {
        absoluteValue,
        absoluteDropOffValue,
        cohortSize,
        columnIdx:
          this.getChartType() === ChartType.Line ? this.getRetentionBucketIndex(matchingShowClauseIndex) : colIdx, // bucket index i.e. < 1 Day, Day 1, Day 2, ...
        rowIdx,
        ...(groupedRowIdx !== null && {groupedRowIdx}), // interval index i.e. Aug 15, 2022, Aug 22, 2022, ...
        completeDate,
        enteredValue,
        enteredDate,
        incomplete,
        isAggregated,
        propertyAggregation: this.getPropertyAggregation(matchingShowClauseIndex),
      },
      i18n,
    );
  }

  isSegmented() {
    return this.bookmark.sections.group.length > 0;
  }

  private getBreakdownCount() {
    return this.bookmark.sections.group.length;
  }

  getBookmarkAsReport() {
    // Utility to get the bookmark in report form (which has some serialization done on it)
    return Report.fromBookmark({params: this.bookmark, type: BookmarkType.Insights, name: ``, description: ``});
  }

  public hasMultipleRetentionMetrics(): boolean {
    return this.getRetentionShowClauses().length > 1;
  }

  flattenData({
    queryResults,
    segments,
    i18n,
  }: {
    queryResults: InsightsQueryResults<Retention>;
    segments: Segment[];
    i18n: LocalizationStore;
  }): {
    segmentKeyToPath: SegmentKeyToPath;
    data: RetentionData;
    compareToPastData?: RetentionData;
    liftData?: RetentionData;
  } {
    if (this.hasMultipleRetentionMetrics()) {
      const results = queryResults.series;
      const pastResults = queryResults.time_comparison && queryResults.time_comparison.series;
      const liftResults = queryResults.lift_comparison && queryResults.lift_comparison.series;
      const {responseData: data, segmentKeyToPath} = flattenMultipleRetentionMetrics(results, segments, i18n);
      const compareToPastData =
        pastResults && flattenMultipleRetentionMetrics(pastResults, segments, i18n).responseData;
      const liftData = liftResults && flattenMultipleRetentionMetrics(liftResults, segments, i18n).responseData;
      return {
        segmentKeyToPath,
        data,
        ...(compareToPastData && {compareToPastData}),
        ...(liftData && {liftData}),
      };
    }
    // first level is always tree
    const results = Object.values(queryResults.series)[0] as unknown as KeyedByDate<Retention>;
    const pastResults =
      queryResults.time_comparison &&
      (Object.values(queryResults.time_comparison.series)[0] as unknown as KeyedByDate<Retention>);
    const liftResults =
      queryResults.lift_comparison &&
      (Object.values(queryResults.lift_comparison.series)[0] as unknown as KeyedByDate<Retention>);
    if (this.isSegmented()) {
      const {responseData: data, segmentKeyToPath} = flattenSegments(results, segments, i18n);
      const compareToPastData = pastResults && flattenSegments(pastResults, segments, i18n).responseData;
      const liftData = liftResults && flattenSegments(liftResults, segments, i18n).responseData;
      return {
        segmentKeyToPath,
        data,
        ...(compareToPastData && {compareToPastData}),
        ...(liftData && {liftData}),
      };
    }
    return {
      segmentKeyToPath: null,
      data: results,
      ...(pastResults && {compareToPastData: pastResults}),
      ...(liftResults && {liftData: liftResults}),
    };
  }

  public getCurveChartData(timezone: string, i18n: LocalizationStore) {
    return this.memoizedGetCurveChartData({
      timezone,
      queryResults: this.queryResults,
      segments: this.getSegments(),
      metricKeyLookup: this.metricKeyLookup,
      bookmark: this.bookmark,
      chartType: this.getChartType(),
      i18n,
    });
  }

  private _getCurveChartData({
    timezone,
    queryResults,
    segments,
    metricKeyLookup,
    bookmark,
    chartType,
    i18n,
  }: GetCurveChartDataArgs) {
    const {segmentKeyToPath, data, compareToPastData, liftData} = this.flattenData({
      queryResults,
      segments,
      i18n,
    });
    const incompleteBuckets = queryResults.meta.report_sections.show[0].incomplete_buckets;
    const pastIncompleteBuckets = queryResults.meta.report_sections.show[0].past_incomplete_buckets;
    let {formattedUiRows, formattedLiftRowsData} = getUiRowsDataFromParsed({
      data,
      incompleteBuckets,
      bookmarkParams: bookmark,
      timezone,
      compareToPastData,
      pastIncompleteBuckets,
      liftData,
      metricKeyLookup,
    });

    // We exclude the overall row from the retention curve chart
    if (chartType === ChartType.RetentionCurve) {
      formattedUiRows = formattedUiRows.filter((row) => row[`head`] !== `$overall`);
    }

    return {chartRows: formattedUiRows, formattedLiftRowsData, segmentKeyToPath};
  }

  percentLabel(value: number, precision = 0) {
    return formatPercent(value, {precision, exactPrecision: true, showSign: false});
  }

  absoluteLabel(value: number, precision?: number) {
    return formatMetricNumber(value, {precision});
  }

  public hasRetentionRateOnly(): boolean {
    const visibleRetentionMetrics = this.getVisibleRetentionMetrics();
    return visibleRetentionMetrics.every((clause) => clause.measurement.math === RETENTION_MATH_TYPES.RETENTION_RATE);
  }

  public formatRetentionCurveXAxisLabel(): (number: number) => Translatable {
    const retentionType = this.getRetentionType();
    const retentionUnit = this.getRetentionUnit();
    const dateUnit = this.getDateUnit();
    const bucketSizes = this.getBucketSizes();
    return function (value: number) {
      return getRetentionBucketName({
        bucketIndex: value,
        retentionType,
        retentionUnit,
        dateUnit,
        bucketSizes,
      });
    };
  }

  public formatRetentionCurveYAxisLabel(metricsResource: MetricsResource) {
    return (y: number, axis: BookmarkTypes.AxisAssignment, yAxisOptions?: LineChartYAxisOptions): string => {
      const defaultOptions = getMetricFormattingDefaultsFromChartParams(this.bookmark, true);
      const {defaultPrimaryOptions, defaultSecondaryOptions} = getDefaultPrimaryAndSecondaryAxisOptions(
        this.bookmark,
        metricsResource,
      );

      const preFormatted = metricFormatterFunctionForAxis(this.bookmark, axis)(y);
      const formatted = formatMetricNumberWithUnits(
        preFormatted,
        mergeDisplayOptions(
          {},
          defaultOptions,
          getDisplayOptionsFromLineChartYAxisOptions(
            axis === BookmarkTypes.AxisAssignment.Primary ? defaultPrimaryOptions : defaultSecondaryOptions,
          ),
          getDisplayOptionsFromLineChartYAxisOptions(yAxisOptions),
        ),
        {abbreviateThousands: true},
      );
      return formatted;
    };
  }

  public isEquivalent(bookmark: InsightsBookmarkParams, results: InsightsQueryResults<Retention>): boolean {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }
}
